import { AppShell } from "../Root/AppShell";
import { Page } from "../../components/Page";
import { usePageTitle } from "../../../../../shared/hooks/usePageTitle";
import { PageActions } from "../../../../../shared/ui/PageActions";
import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { reportError } from "../../../../../shared/errorReporting";
import { useTranslation } from "../../i18n/useTranslation";
import { isModuleLoadFailure } from "../../../../../shared/lib/moduleLoadFailure";
import "./Error.css";

/**
 * A route's lazy chunk failing to load is NOT a 404, and saying so on the
 * Stripe return leg would be actively misleading.
 *
 * The realistic trigger is a redeploy: a browser holding a stale index.html
 * requests an old hashed chunk, the SPA catch-all answers with index.html at
 * HTTP 200 and Content-Type text/html, and `import()` rejects on the MIME type.
 * A reload picks up the new index.html and recovers. The detection is
 * shared/lib/moduleLoadFailure.ts, shared with Book's RouteError.
 */

/**
 * The ROOT route's errorElement, which brings its own shell.
 *
 * Every other screen is centred by `Root`'s `.shell` / `.shell-main`. The root
 * errorElement renders INSTEAD of `Root` rather than inside it (a URL nothing
 * matches, or Root itself failing), so there is no shell above it, and without
 * this wrapper the card lands hard against the top-left corner with its copy
 * overflowing the edge.
 *
 * Found by looking at it. The unit tests passed throughout, because they assert
 * what the document says and this was entirely a question of what it looked
 * like.
 */
export default function Error() {
  return (
    <AppShell>
      <RouteError />
    </AppShell>
  );
}

/**
 * ONE SHELL (c2), for errors below Root: the errorElement of the pathless
 * AuthLayout route, which renders in Root's Outlet, INSIDE the AppShell that is
 * already on screen. A lazy chunk that fails on the first load (or on a later
 * navigation) then replaces only the page: the header, footer and theme toggle
 * are the same nodes, so nothing focused in them is lost. Same states and copy
 * as the root's (stale chunk or 404).
 */
export function InShellError() {
  return <RouteError />;
}

function RouteError() {
  const { t } = useTranslation();
  const error = useRouteError();
  console.error(error);

  // A thrown error is reported; a router 404 (no route matched) is a wrong
  // URL, not a fault, and is not. Scrubbing and the allowlist live in
  // shared/errorReporting; this only hands the error over.
  useEffect(() => {
    if (!isRouteErrorResponse(error)) reportError(error);
  }, [error]);

  // `Error` here is the component, so reach for the global explicitly.
  const detail =
    error instanceof globalThis.Error ? error.message : JSON.stringify(error);
  const stale = isModuleLoadFailure(error);
  usePageTitle(t(stale ? "error.stale.title" : "error.notFound.title"));

  if (stale) {
    return (
      <Page title={t("error.stale.title")} testId="error-stale-chunk">
        <p className="w2-hint" data-testid="error-stale-chunk-fallback">
          {t("error.stale.fallback")}
        </p>
        {import.meta.env.DEV ? <pre>{detail}</pre> : null}
        <PageActions className="w2-foot">
          <button type="button" onClick={() => window.location.reload()}>
            {t("error.stale.reload")}
          </button>
        </PageActions>
      </Page>
    );
  }

  return (
    <Page title={t("error.notFound.title")} testId="error-not-found">
      {/* The number stays, small, because it is useful to anybody
          reporting it. Decoration, not the heading. */}
      <p className="w2-hint" aria-hidden="true">
        404
      </p>
      {import.meta.env.DEV && error ? <pre>{detail}</pre> : null}
      <PageActions><a className="w2-btn" href="https://uselura.com" referrerPolicy="no-referrer">{t("common.openLura")}</a></PageActions>
    </Page>
  );
}
